import { DateTime } from "luxon";

import type { ReportDiscoveryFilterField, ReportDiscoveryFilterOperator } from "../../api/generated/graphql";
import type { ReportingDiscoveryScope } from "../../ui/Reporting/Discovery/reportingDiscoveryScopes";
import type { ReportingDiscoveryTimePeriod } from "../../ui/Reporting/Discovery/reportingDiscoveryTimePeriods";

import type { WaterlyAppStateCreator } from "./waterlyAppStateCreator.type";

type ReportingDiscoveryGridFilter = {
  field: ReportDiscoveryFilterField;
  operator: ReportDiscoveryFilterOperator;
  values?: string[] | null;
};

type ReportingDiscoveryState = {
  discoveryBulkDownloadDailyDate: DateTime;
  discoveryBulkDownloadFormat: "PDF" | "XLS";
  discoveryBulkDownloadMonthlyDate: DateTime;
  discoveryOrganizationId: number | null;
  discoveryScopes: ReportingDiscoveryScope[];
  discoveryTimePeriods: ReportingDiscoveryTimePeriod[];
  discoverySystemIds: number[];
  discoveryGridFilters: ReportingDiscoveryGridFilter[];
  discoveryClearCount: number;
};

type ReportingDiscoveryActions = {
  setDiscoveryBulkDownloadDailyDate: (date: DateTime) => void;
  setDiscoveryBulkDownloadFormat: (format: "PDF" | "XLS") => void;
  setDiscoveryBulkDownloadMonthlyDate: (date: DateTime) => void;
  setDiscoveryOrganizationId: (id: number | null) => void;
  setDiscoveryScopes: (scopes: ReportingDiscoveryScope[]) => void;
  setDiscoveryTimePeriods: (timePeriods: ReportingDiscoveryTimePeriod[]) => void;
  setDiscoverySystemIds: (systemIds: number[]) => void;
  setDiscoveryGridFilters: (filters: ReportingDiscoveryGridFilter[]) => void;
  initDiscoveryFilters: (state: {
    discoveryOrganizationId: number | null;
    discoveryScopes: ReportingDiscoveryScope[];
    discoveryTimePeriods: ReportingDiscoveryTimePeriod[];
    discoverySystemIds: number[];
  }) => void;
  clearDiscoveryFilters: (defaults: {
    discoveryOrganizationId: number | null;
    discoverySystemIds: number[];
  }) => void;
};

const initialState: ReportingDiscoveryState = {
  discoveryBulkDownloadDailyDate: DateTime.now(),
  discoveryBulkDownloadFormat: "PDF",
  discoveryBulkDownloadMonthlyDate: DateTime.now().startOf("month"),
  discoveryOrganizationId: null,
  discoveryScopes: [],
  discoveryTimePeriods: [],
  discoverySystemIds: [],
  discoveryGridFilters: [],
  discoveryClearCount: 0,
};

export const createReportingDiscoverySlice: WaterlyAppStateCreator<
  ReportingDiscoveryActions,
  ReportingDiscoveryState
> = (set) => ({
  ...initialState,
  setDiscoveryBulkDownloadDailyDate: (date) => set({ discoveryBulkDownloadDailyDate: date }),
  setDiscoveryBulkDownloadFormat: (format) => set({ discoveryBulkDownloadFormat: format }),
  setDiscoveryBulkDownloadMonthlyDate: (date) => set({ discoveryBulkDownloadMonthlyDate: date }),
  setDiscoveryOrganizationId: (id) => set({ discoveryOrganizationId: id }),
  setDiscoveryScopes: (scopes) => set({ discoveryScopes: scopes }),
  setDiscoveryTimePeriods: (timePeriods) => set({ discoveryTimePeriods: timePeriods }),
  setDiscoverySystemIds: (systemIds) => set({ discoverySystemIds: systemIds }),
  setDiscoveryGridFilters: (filters) => set({ discoveryGridFilters: filters }),
  initDiscoveryFilters: (state) => set(state),
  clearDiscoveryFilters: (defaults) =>
    set((s) => ({
      discoveryOrganizationId: defaults.discoveryOrganizationId,
      discoveryScopes: [],
      discoveryTimePeriods: [],
      discoverySystemIds: defaults.discoverySystemIds,
      discoveryGridFilters: [],
      discoveryClearCount: s.discoveryClearCount + 1,
    })),
});
